import {
  parseExistingAgreements,
  convertAppDataToTrustFundFormData,
  convertAppDataToTrustRemittanceFormData,
  loadInvestmentDetails,
  addAdditionalCustomers,
  checkTrustPartialAllocation
} from './utils';
import {
  pollForSubmissionResponse,
  appendMailingAddressIfDifferent,
  commitConsent,
  getUserExistingAgreements,
  populateResidentialAddress
} from '../../shared/loaders';
import {
  parsePhone,
  setTaxIDType,
  accountTypes,
  consumerTypes,
  PhoneNumberType,
  getOriginalResidentialAddress,
  getCorrectedResidentialAddress,
  getCorrectedMailingAddress,
  getOriginalMailingAddress,
  fundingTypes,
  sourceTypes,
  sumOf,
  investmentMethods,
  formatTwoDecimalAmountNoRounding,
  secondaryCommitMessage,
  applicationStatusTypes,
  createFormURL
} from '../../shared/utils';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../../analytics/analytics-helper';
import {
  accountTypesAnalytics,
  productTypesAnalytics
} from '../../../analytics/util';
import {
  merge,
  mergeTyped,
  withDefault,
  removeCommasAndParseFloat
} from '../../../core/util';

import { commitNewInvestmentsBYOP } from '../shared/loaders';
import { formatDate } from '../../shared/dateUtils';
import { DataLoader } from '../../../core/types/data-loader';

import {
  ApplicationState,
  OptionalStaticApplication,
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../../core/types/application-state';
import {
  authenticateUser,
  getEnablementStatusService,
  registerAndLoginUserService,
  getUserValidationStatusService
} from '../../shared/services/user-service';
import { ServiceError } from '../../shared/services';
import { postReferrer } from '../../shared/services/referrer-service';
import { postAddressValidation } from '../../shared/services/address-validation-service';
import { StaticCoTrustee } from '../../../core/types/additional-customers';
import {
  getAgreementParties,
  getExistingAgreementsService
} from '../../shared/services/existing-agreements-service';
import { TrustApplicationService } from '../../shared/services/mutualfund-taxable-trust';
import { StaticAddress } from '../../../core/types/address';
import { postTrustFundFormData } from '../../shared/services/trustfund-ownership-form-service';
import { postCheckRemittanceFormData } from '../../shared/services/check-remittance.service';
import { createNewReturnJourneyProfile } from '../../return-journey/loaders';
import { getExistingApplications } from '../../shared/services/profile-service';
import { getRewardsAvailable } from '../../shared/summit.utils';
import { getFundAllocationsService } from '../../shared/services/allocation-service';
import { encode_deviceprint as encodeDeviceprint } from '../../../rsa-fingerprint';
import { isMailingAddressDifferent } from '../../front-office/shared/utils';

// COMMIT FUNCTIONS
export const getEnablementStatus: DataLoader = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs: { ssn, dob, firstName, lastName }
  } = appState;

  const {
    store: {
      enabled,
      app: { trustee }
    }
  } = appState;

  const taxID = enabled ? trustee?.taxID : ssn?.replace(/[^*0-9]/g, '');

  const userValidation = await getUserValidationStatusService({
    firstName,
    lastName,
    taxID,
    dateOfBirth: formatDate(dob, 'MM/DD/YYYY', '')
  });

  if (userValidation?.validationStatus === 'INVALID') {
    return {
      app: {},
      loadedData: userValidation
    };
  }

  const userstatus = await getEnablementStatusService({
    firstName,
    lastName,
    taxID,
    dateOfBirth: formatDate(dob, 'MM/DD/YYYY', '')
  });

  return {
    app: {},
    enabled: withDefault(userstatus?.enabled, enabled),
    loadedData: { ...userstatus, ...userValidation }
  };
};

export const commitProfileData: DataLoader = async ({
  chosen,
  inputs,
  store
}): Promise<StateStoreOrVoid> => {
  if (chosen === 'skipLogin') {
    return {};
  }

  const shoppingFunds = chosen;
  const { amount, firstName, lastName, primaryEmailAddress } = inputs;
  const { accountType, app } = store;
  // account for defaulted input values not persisting for customers with existing data
  const defaultedProfileData = {
    primaryEmailAddress: withDefault(
      app?.trustee?.primaryEmailAddress,
      primaryEmailAddress
    ),
    firstName: withDefault(app?.trustee?.name?.firstName, firstName),
    lastName: withDefault(app?.trustee?.name?.lastName, lastName)
  };
  const existingApplications = await getExistingApplications(
    defaultedProfileData.primaryEmailAddress,
    accountType
  );

  // filter applications which does not belong to 'FRONT_OFFICE_NASU'
  const filterExistingApps =
    Array.isArray(existingApplications) &&
    existingApplications?.filter(
      appID => appID.consumerName !== consumerTypes.FONASU
    );

  // get Profile ID of first application
  const existingID = filterExistingApps?.[0]?.applicationID;

  const storeToCommit = {
    accountType,
    // fallback to fetch amount value from store, incase of modal action
    // wiping out the input value
    amount: removeCommasAndParseFloat(withDefault(amount, store?.amount)),
    app: {
      trustee: {
        name: {
          firstName: defaultedProfileData.firstName,
          lastName: defaultedProfileData.lastName
        },
        primaryEmailAddress: defaultedProfileData.primaryEmailAddress
      },
      fundingMethodType: fundingTypes.CHECK
    },
    ...(shoppingFunds && { loadedData: { shoppingFunds } })
  };

  if (!existingID) return createNewReturnJourneyProfile(storeToCommit);

  return {
    ...storeToCommit,
    app: { ...storeToCommit.app, profileApplicationID: existingID }
  };
};

/* eslint-disable complexity */
export const commitTrustFundingMethodType: DataLoader = ({
  store,
  chosen,
  savedInputs
}): PartialStateStore => {
  const fundingViaExisting = store?.loadedData?.isExistingFundsUsed;
  const fundingChangedToCheck =
    chosen === fundingTypes.CHECK && Boolean(fundingViaExisting);

  return {
    amount: store.amount,
    app: {
      fundingMethodType: chosen,
      accountTransferDetails: undefined,
      coTrustees: [],
      ...(fundingChangedToCheck && { newInvestments: [] })
    },
    ...(chosen === fundingTypes.CHECK && {
      loadedData: {
        isExistingFundsUsed: false
      }
    }),
    ...(fundingChangedToCheck && {
      ...(savedInputs && {
        amount: removeCommasAndParseFloat(
          savedInputs['mutual-fund/general-investing/trust']?.amount
        )
      }),
      chosenInvestments: []
    })
  };
};
/* eslint-enable complexity */

export const commitExistingInvestments: DataLoader = async ({ chosen }) => {
  const agreementParties = await getAgreementParties(chosen[0].agreementID);

  // DE53208 Filter out Existing Agreements with non zero investment amount
  const filterExistingInvestments = chosen.filter(
    estInvAmount => estInvAmount.balanceAsOf !== '0'
  );

  const newInvestments = filterExistingInvestments.map(
    ({ ticker, trpCode, balanceAsOf }) => ({
      ticker,
      trpCode,
      fundingAmount: parseFloat(balanceAsOf)
    })
  );

  // take sum of fundingAmounts given to each investment, and update amount within store
  const updatedAmount = parseFloat(
    formatTwoDecimalAmountNoRounding(
      sumOf(newInvestments.map(inv => inv.fundingAmount))
    )
  );

  // set up account transfer information for chosen existing agreement
  const accountTransferDetails = filterExistingInvestments.map(
    ({ agreementID, balanceAsOf }) => ({
      agreementId: agreementID,
      redemptionAmount: parseFloat(balanceAsOf)
    })
  );

  // if more than one party (primary owner) is listed on the agreement, then populate
  // cotrustees with the additional secondary owner data
  const coTrustees: StaticCoTrustee[] = [];
  if (agreementParties?.length > 1) {
    agreementParties.filter(
      ({
        primaryParty,
        name: { firstName, middleName = '', lastName },
        dateOfBirth,
        citizenshipType,
        taxID
      }) =>
        !primaryParty &&
        coTrustees.push({
          name: { firstName, middleName, lastName },
          citizenship: citizenshipType,
          dob: formatDate(dateOfBirth, 'MM/DD/YYYY', ''),
          taxID,
          taxIDType: setTaxIDType(citizenshipType)
        })
    );
  }

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    app: {
      newInvestments,
      accountTransferDetails,
      investmentMethod: investmentMethods.EXISTING,
      ...(coTrustees.length > 0 && {
        coTrustees
      })
    },
    amount: updatedAmount,
    chosenInvestments: chosen,
    ...(coTrustees.length > 0 && {
      additionalCustomers: coTrustees.map(co => ({
        ...co,
        customerType: 'TRUSTEE'
      }))
    }),
    loadedData: {
      isExistingFundsUsed: true,
      partialExistingFunds: checkTrustPartialAllocation(chosen)
    }
  };
};

export const commitTrustInformation = (appState): PartialStateStore => {
  const {
    inputs,
    store: { app }
  } = appState;

  const {
    trustID = '',
    trustName,
    trustDate,
    trustIDType,
    trustStreet1,
    trustStreet2,
    trustZip,
    trustCity,
    trustState
  } = inputs;

  const taxID = trustID.replace(/[^*0-9]/g, '');

  const res = {
    app: {
      ...app,
      trust: {
        name: trustName,
        trustDate: formatDate(trustDate, 'MM/DD/YYYY', ''),
        taxID,
        taxIDSSN: trustIDType === 'SSN',
        address: {
          street1: trustStreet1,
          street2: trustStreet2,
          zip: trustZip,
          city: trustCity,
          state: trustState
        }
      }
    }
  };

  return res;
};

export const handleShoppingFundsAndRewards = async (
  shoppingFunds,
  rewardsAvailable
): Promise<any> => {
  const {
    closedFunds: closedFundReward,
    iClassFunds: iClassFundReward
  } = rewardsAvailable;

  const tickers = shoppingFunds.map(f => f.ticker);

  const funds = await getFundAllocationsService({ tickers });

  let cleanedFunds = funds;

  if (!closedFundReward)
    cleanedFunds = cleanedFunds.filter(fund => fund.openToNewInvestors);
  if (!iClassFundReward)
    cleanedFunds = cleanedFunds.filter(fund => !fund.iClass);

  const fundsContainer =
    shoppingFunds.length !== cleanedFunds.length
      ? {
          chosenInvestments: cleanedFunds,
          loadedData: { shoppingFunds: [] }
        }
      : {
          loadedData: { shoppingFunds: cleanedFunds }
        };

  return fundsContainer;
};

export const commitTrusteeInformation = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs,
    store: { enabled, app, loadedData = {}, summitData = {} },
    chosen
  } = appState;
  const { trust, trustee } = app;
  const {
    ssn = '',
    firstName,
    lastName,
    middleName = '',
    dob,
    citizenship = '',
    people = []
  } = inputs;

  const { shoppingFunds = [] } = loadedData;
  const { assetTierID = 0 } = summitData;

  let res;

  if (Array.isArray(chosen)) {
    // user came from "update to iclass" modal
    res = commitNewInvestmentsBYOP(appState);
  } else {
    // proceed with committing PI
    const taxID = enabled ? trustee?.taxID : ssn?.replace(/[^*0-9]/g, '');
    const enablementStatus = await getEnablementStatus(appState);

    const rewardsAvailable = getRewardsAvailable(
      accountTypes.TRUST,
      trust,
      trustee,
      assetTierID
    );

    const newApp = {
      trustee: {
        name: {
          firstName,
          lastName,
          middleName
        },
        dob: formatDate(dob, 'MM/DD/YYYY', ''),
        citizenship: withDefault(inputs.citizenship, trustee?.citizenship),
        taxID,
        taxIDType: setTaxIDType(citizenship)
      },
      coTrustees: people.length
        ? addAdditionalCustomers(true, people)
        : undefined
    };
    res = merge(
      {
        app: newApp,
        ...(shoppingFunds.length && {
          ...(await handleShoppingFundsAndRewards(
            shoppingFunds,
            rewardsAvailable
          ))
        }),
        additionalCustomers: addAdditionalCustomers(false, people)
      },
      enablementStatus
    );
  }

  return res;
};

/* eslint-disable complexity */
// TODO @Dis(Heather) [08-31-2021] - fix complexity
export const commitAddressValidation = async ({
  inputs,
  store,
  chosen
}): Promise<StateStore> => {
  const {
    app: { trustee }
  } = store;

  // If user clicks on Cancel/Close icon on the modal and open the modal again,
  // need to fetch the previously entered values from the store
  const residentialStreet1 = withDefault(
    inputs.residentialStreet1,
    store?.residentialAddressValidation?.originalAddress?.street1
  );
  const residentialStreet2 = withDefault(
    inputs.residentialStreet2,
    store?.residentialAddressValidation?.originalAddress?.street2 || ''
  );
  const residentialZip = withDefault(
    inputs.residentialZip,
    store?.residentialAddressValidation?.originalAddress?.zip
  );
  const residentialCity = withDefault(
    inputs.residentialCity,
    store?.residentialAddressValidation?.originalAddress?.city
  );
  const residentialState = withDefault(
    inputs.residentialState,
    store?.residentialAddressValidation?.originalAddress?.state
  );
  const { phone1, phone2, phone3 } = inputs;

  const mailingAddressDifferent = withDefault(
    inputs?.mailingAddressDifferent,
    chosen?.mailingAddressDiffValue
  );
  const primaryEmailAddress = withDefault(
    inputs?.primaryEmailAddress,
    trustee?.primaryEmailAddress
  );

  const residentialAddress = {
    city: residentialCity.trim(),
    country: 'United States',
    state: residentialState.trim(),
    street1: residentialStreet1.trim(),
    street2: residentialStreet2?.trim(),
    street3: '',
    usaddress: true,
    zip: residentialZip.trim()
  };

  let mailingAddress = {};
  if (mailingAddressDifferent) {
    // If user clicks on Cancel/Close icon on the modal and open the modal again,
    // need to fetch the previously entered values from the store
    const mailingStreet1 = withDefault(
      inputs.mailingStreet1,
      store?.mailingAddressValidation?.originalAddress?.street1
    );
    const mailingStreet2 = withDefault(
      inputs.mailingStreet2,
      store?.mailingAddressValidation?.originalAddress?.street2 || ''
    );
    const mailingZip = withDefault(
      inputs.mailingZip,
      store?.mailingAddressValidation?.originalAddress?.zip
    );
    const mailingCity = withDefault(
      inputs.mailingCity,
      store?.mailingAddressValidation?.originalAddress?.city
    );
    const mailingState = withDefault(
      inputs.mailingState,
      store?.mailingAddressValidation?.originalAddress?.state
    );

    mailingAddress = {
      city: mailingCity.trim(),
      country: 'United States',
      state: mailingState.trim(),
      street1: mailingStreet1.trim(),
      street2: mailingStreet2?.trim(),
      street3: '',
      usaddress: true,
      zip: mailingZip.trim()
    };
  }

  let addresses = [residentialAddress];
  addresses = appendMailingAddressIfDifferent(
    addresses,
    mailingAddressDifferent,
    mailingAddress
  );

  let phones = [
    { phone: phone1, type: PhoneNumberType.CELLULAR },
    {
      phone: phone2,
      type: PhoneNumberType.DAYTIME
    },
    { phone: phone3, type: PhoneNumberType.EVENING }
  ]
    .filter(({ phone }) => phone)
    .map(({ phone, type }) => parsePhone(phone, type));

  if (phones?.length < 1) phones = trustee?.phones;

  const [
    residentialAddressValid,
    mailingAddressValid
  ] = await postAddressValidation(addresses);
  const residentialAddressValidation =
    residentialAddressValid || residentialAddress;
  const mailingAddressValidation = mailingAddressValid || mailingAddress;

  // need to commit email address in the case user has updated it since basic pi
  // otherwise modal action will wipe any user updates to email address
  return {
    residentialAddressValidation,
    ...(mailingAddressDifferent && { mailingAddressValidation }),
    mailingAddressDifferent,
    app: {
      trustee: {
        phones,
        primaryEmailAddress
      }
    }
  };
};

export const commitCoTrusteeAddress = (
  app: OptionalStaticApplication,
  address: StaticAddress
): any[] => {
  const { coTrustees } = app;

  if (!coTrustees) return [];

  const coTrusteesWithAddress = coTrustees?.map(co => ({
    ...co,
    residentialAddress: address
  }));

  return coTrusteesWithAddress;
};
export const commitAdditionalCustomersAddress = (
  store: PartialStateStore,
  address: StaticAddress
): any[] => {
  const { additionalCustomers = [] } = store;

  if (!additionalCustomers.length) return [];

  const additionalCustomersWithAddress = additionalCustomers?.map(customer => ({
    ...customer,
    residentialAddress: address
  }));

  return additionalCustomersWithAddress;
};

// TODO @Dis(Heather) [08-31-2021] - fix complexity
export const commitTrusteeContactInformation: DataLoader = async ({
  inputs,
  chosen,
  store
}): Promise<StateStore> => {
  if (chosen?.section === 'addressValidation') {
    return commitAddressValidation({ inputs, store, chosen });
  }

  const { useResidentialAddress, useMailingAddress } = inputs;
  const {
    residentialAddressValidation,
    mailingAddressValidation = {},
    mailingAddressDifferent
  } = store;

  const originalResidentialAddress = getOriginalResidentialAddress(
    residentialAddressValidation
  );
  const correctedResidentialAddress = getCorrectedResidentialAddress(
    residentialAddressValidation
  );
  const residentialAddress = populateResidentialAddress(
    useResidentialAddress,
    originalResidentialAddress,
    correctedResidentialAddress
  );

  const originalMailingAddress = getOriginalMailingAddress(
    mailingAddressValidation
  );
  const correctedMailingAddress = getCorrectedMailingAddress(
    mailingAddressValidation
  );
  let mailingAddress = residentialAddress;
  if (mailingAddressDifferent) {
    if (useMailingAddress === 'useOriginalMailingAddress') {
      const { street1, street2, city, state, zip } = originalMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        corrected: false
      };
    } else {
      const {
        street1,
        street2,
        city,
        state,
        zip,
        zip4
      } = correctedMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        zip4,
        corrected: true
      };
    }
  }

  const { app } = store;
  // update cotrustee information for residential address
  // set to match with the trustee(aka customer) residential address
  const coTrustees = commitCoTrusteeAddress(app, residentialAddress);
  const updatedAdditionalCustomers = commitAdditionalCustomersAddress(
    store,
    residentialAddress
  );

  return {
    app: {
      trustee: {
        residentialAddress,
        mailingAddress
      },
      ...(coTrustees?.length && { coTrustees })
    },
    ...(updatedAdditionalCustomers.length && {
      additionalCustomers: updatedAdditionalCustomers
    })
  };
};
/* eslint-enable complexity */

export const registerAndLoginUser: DataLoader = async ({
  store: {
    app: { trustee },
    authenticated,
    loadedData
  },
  inputs: { username: loginID, password }
}) => {
  if (!authenticated) {
    try {
      await registerAndLoginUserService({
        firstName: trustee?.name?.firstName,
        lastName: trustee?.name?.lastName,
        emailAddress: trustee?.primaryEmailAddress,
        loginID: withDefault(loginID, loadedData?.loginID),
        password: withDefault(password, loadedData?.password)
      });
    } catch (err) {
      const usernameTaken = Boolean(
        err.toString().match(/Username already exists/)
      );
      if (usernameTaken) {
        return {
          loadedData: {
            usernameTaken,
            loginID: withDefault(loginID, loadedData?.loginID),
            password: withDefault(password, loadedData?.password)
          }
        };
      }

      // if the error has nothing to do with username being taken - rethrow
      throw new ServiceError(err);
    }
  }

  return {
    authenticated: true,
    loadedData: { usernameTaken: false }
  };
};

export const createTrustApplication: DataLoader = async ({
  inputs,
  store,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (
    [
      'existing-account',
      'byop',
      'rdf',
      'funding',
      'info',
      'trust',
      'trustee'
    ].includes(chosen)
  ) {
    return { inReviewMode: true };
  }

  const { app } = store;
  const { trustee, termsIDs } = app;
  const { eligibleForTaxWithholding = false } = inputs;

  // use terms ids to create consent
  await commitConsent(termsIDs, trustee);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());
  // add rollover type and term IDs to account creation service payload
  const updatedAppState = {
    ...app,
    eligibleForTaxWithholding,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // clean up payload to make sure customer object isn't present
  // customer data will be stored in trustee object for Trust accounts
  delete updatedAppState.customer;
  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await TrustApplicationService(updatedAppState);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
// specifies loading message when creating account at end
createTrustApplication.message = 'Creating your new account';
createTrustApplication.secondaryMessage = secondaryCommitMessage;

export const commitReferrerForm: DataLoader = async ({
  store,
  chosen,
  inputs
}): Promise<PartialStateStore> => {
  // Bypasses commiting to store when not needed
  if (chosen === 'guide') return { loadedData: { referrerError: false } };

  const { refFullName = '', refEmail = '' } = inputs;

  if (refFullName.trim() === '' || refEmail.trim() === '') {
    return { loadedData: { referrerError: true } };
  }

  const {
    app: { trustee }
  } = store;

  if (chosen === 'referrer') {
    const customerFullName = `${trustee?.name?.firstName} ${trustee?.name?.lastName}`;
    const refPayload = {
      referrerName: refFullName,
      referrerEmailAddress: refEmail,
      referredName: customerFullName,
      referredEmailAddress: trustee?.primaryEmailAddress
    };

    await postReferrer(refPayload);
  }

  return {
    loadedData: { submittedReferrer: true, referrerError: false }
  };
};

// LOAD FUNCTIONS

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  analyticsDataLayer({
    accountType: accountTypesAnalytics.TAXABLE.TRUST.NEW,
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: sourceTypes.DEFAULT,
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New',
    amount: store?.amount,
    isComplete: Boolean(flowProps?.analytics?.isComplete),
    clientTier: store?.summitData?.assetTierID?.toString()
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};

export const loadUser: DataLoader = async (
  appState: ApplicationState,
  accountType
): Promise<StateStore> => {
  const {
    authenticated,
    enabled,
    prospect,
    trustee,
    summitData
  } = await authenticateUser(appState.store, accountType);

  return {
    authenticated: Boolean(authenticated),
    enabled: Boolean(enabled),
    prospect: Boolean(prospect),
    userStateLoaded: true,
    summitData,
    app: { trustee },
    mailingAddressDifferent: isMailingAddressDifferent(trustee),
    loadedData: {
      citizenShipPresent: Boolean(trustee?.citizenship)
    }
  };
};

export const loadExistingAgreements: DataLoader = async () => {
  const existingAgreementsResults = await getExistingAgreementsService([
    accountTypes.INDIVIDUAL,
    accountTypes.JOINT,
    accountTypes.TOD
  ]);
  const groupedExistingAccounts = parseExistingAgreements(
    existingAgreementsResults
  );

  return {
    existingAgreements: groupedExistingAccounts
  };
};

export const makeLoadProfileData = (): DataLoader =>
  async function loadProfileData(
    a: ApplicationState,
    flowProps
  ): Promise<StateStoreOrVoid> {
    const userResults = await loadUser(a, flowProps?.accountType);

    const existingAccountAgreementsResults = await getUserExistingAgreements(
      userResults,
      [accountTypes.INDIVIDUAL, accountTypes.JOINT, accountTypes.TOD]
    );
    const groupedExistingAccounts = parseExistingAgreements(
      existingAccountAgreementsResults
    );

    return {
      ...(groupedExistingAccounts && {
        existingAgreements: groupedExistingAccounts
      }),
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        accountType: flowProps?.accountType
      })
    };
  };

export const loadTrustSubmissionForm: DataLoader = async ({
  store
}): Promise<PartialStateStore> => {
  const {
    app: { fundingMethodType, newInvestments = [] }
  } = store;

  let payload;
  let resBlob;

  if (fundingMethodType !== fundingTypes.CHECK) {
    payload = convertAppDataToTrustFundFormData(store);
    resBlob = await postTrustFundFormData(payload);
  } else {
    const fundDetails = await loadInvestmentDetails(newInvestments);
    payload = convertAppDataToTrustRemittanceFormData(store, fundDetails);
    resBlob = await postCheckRemittanceFormData(payload);
  }

  const formURL = createFormURL(resBlob);
  const loadedData = { ...store.loadedData, formURL };
  return { loadedData };
};
